import { type FormEvent, useId, useRef, useState } from "react";
import { PlusIcon, Trash2Icon } from "lucide-react";

import { Button } from "@/lib/shadcn/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/lib/shadcn/ui/dialog";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import type { CreateNeteraAccountRequest, NewNeteraTarget } from "@/models/responses";
import { useCreateNeteraAccountMutation } from "@/store/filing-credentials-api";

import { failureFor, type RequestError, serverMessageOf, statusOf } from "./filing-credentials.errors";
import { FieldError, Note, SecretInput } from "./filing-credentials.parts";

type DraftTarget = NewNeteraTarget & { key: number };

interface Draft {
    username: string;
    password: string;
    apiKey: string;
    targets: DraftTarget[];
}

interface Failure {
    message: string;
    invalidInput: boolean;
}

const NOT_ADDED = "the account was not added";

const creationFailures: Record<number, string> = {
    422: `Netera rejected this login — ${NOT_ADDED}`,
    423: `The Netera account is locked for a few minutes — try again later; ${NOT_ADDED}`,
    502: `Could not reach Netera — ${NOT_ADDED}`,
};

function creationFailure(error: RequestError): Failure {
    const status = statusOf(error);
    const serverMessage = status === 400 || status === 409 ? serverMessageOf(error) : undefined;
    return {
        message: serverMessage ?? failureFor(error, creationFailures, "The account was not added. Try again."),
        invalidInput: status === 400,
    };
}

function fieldMarks(errorId: string | undefined) {
    return { "aria-required": true, "aria-invalid": errorId ? true : undefined, "aria-describedby": errorId } as const;
}

const blankTarget = (key: number): DraftTarget => ({ key, label: "", hotelId: "", branchId: "" });

const isFilled = (value: string): boolean => value.trim() !== "";

function isComplete(draft: Draft): boolean {
    return (
        [draft.username, draft.password, draft.apiKey].every(isFilled) &&
        draft.targets.every((target) => [target.label, target.hotelId, target.branchId].every(isFilled))
    );
}

function requestFrom(draft: Draft): CreateNeteraAccountRequest {
    return {
        username: draft.username.trim(),
        password: draft.password,
        apiKey: draft.apiKey,
        targets: draft.targets.map((target) => ({
            label: target.label.trim(),
            hotelId: target.hotelId.trim(),
            branchId: target.branchId.trim(),
        })),
    };
}

function TargetRow({
    position,
    target,
    removable,
    errorId,
    onChange,
    onRemove,
}: {
    position: number;
    target: DraftTarget;
    removable: boolean;
    errorId: string | undefined;
    onChange: (target: DraftTarget) => void;
    onRemove: () => void;
}) {
    const name = `Filing target ${position}`;
    const marks = fieldMarks(errorId);
    return (
        <div
            role="group"
            aria-label={name}
            data-testid={`new-target-${position}`}
            className="grid grid-cols-[1fr_1fr_auto] gap-2 sm:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem_auto]"
        >
            <Input
                data-testid="new-target-label"
                aria-label={`${name} label`}
                placeholder="Label"
                {...marks}
                value={target.label}
                onChange={(event) => onChange({ ...target, label: event.target.value })}
                className="col-span-3 sm:col-span-1"
            />
            <Input
                data-testid="new-target-hotel-id"
                aria-label={`${name} hotel ID`}
                placeholder="Hotel ID"
                {...marks}
                inputMode="numeric"
                value={target.hotelId}
                onChange={(event) => onChange({ ...target, hotelId: event.target.value })}
                className="tabular-nums"
            />
            <Input
                data-testid="new-target-branch-id"
                aria-label={`${name} branch ID`}
                placeholder="Branch ID"
                {...marks}
                inputMode="numeric"
                value={target.branchId}
                onChange={(event) => onChange({ ...target, branchId: event.target.value })}
                className="tabular-nums"
            />
            <Button
                type="button"
                variant="ghost"
                size="icon"
                data-testid="new-target-remove"
                aria-label={`Remove ${name.toLowerCase()}`}
                disabled={!removable}
                onClick={onRemove}
                className="text-muted-foreground hover:text-destructive"
            >
                <Trash2Icon className="h-4 w-4" aria-hidden="true" />
            </Button>
        </div>
    );
}

function CredentialFields({
    draft,
    errorId,
    onChange,
}: {
    draft: Draft;
    errorId: string | undefined;
    onChange: (draft: Draft) => void;
}) {
    const ids = useId();
    const marks = fieldMarks(errorId);
    return (
        <div className="space-y-3">
            <div className="space-y-1.5">
                <Label htmlFor={`${ids}-username`}>Username</Label>
                <Input
                    id={`${ids}-username`}
                    data-testid="new-account-username"
                    autoComplete="off"
                    spellCheck={false}
                    value={draft.username}
                    onChange={(event) => onChange({ ...draft, username: event.target.value })}
                    {...marks}
                    autoFocus
                />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                    <Label htmlFor={`${ids}-password`}>Password</Label>
                    <SecretInput
                        id={`${ids}-password`}
                        testId="new-account-password"
                        secret="password"
                        autoComplete="new-password"
                        value={draft.password}
                        onChange={(event) => onChange({ ...draft, password: event.target.value })}
                        {...marks}
                    />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor={`${ids}-api-key`}>API key</Label>
                    <SecretInput
                        id={`${ids}-api-key`}
                        testId="new-account-api-key"
                        secret="API key"
                        autoComplete="off"
                        value={draft.apiKey}
                        onChange={(event) => onChange({ ...draft, apiKey: event.target.value })}
                        {...marks}
                    />
                </div>
            </div>
        </div>
    );
}

function TargetsFieldset({
    targets,
    errorId,
    onReplace,
    onRemove,
    onAdd,
}: {
    targets: DraftTarget[];
    errorId: string | undefined;
    onReplace: (target: DraftTarget) => void;
    onRemove: (key: number) => void;
    onAdd: () => void;
}) {
    const addButton = useRef<HTMLButtonElement>(null);

    const remove = (key: number) => {
        addButton.current?.focus();
        onRemove(key);
    };

    return (
        <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Filing targets</legend>
            <div
                aria-hidden="true"
                className="hidden grid-cols-[minmax(0,1fr)_6.5rem_6.5rem_2.25rem] gap-2 text-xs text-muted-foreground sm:grid"
            >
                <span>Label</span>
                <span>Hotel ID</span>
                <span>Branch ID</span>
            </div>
            <div className="space-y-3 sm:space-y-2">
                {targets.map((target, index) => (
                    <TargetRow
                        key={target.key}
                        position={index + 1}
                        target={target}
                        removable={targets.length > 1}
                        errorId={errorId}
                        onChange={onReplace}
                        onRemove={() => remove(target.key)}
                    />
                ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <p data-testid="new-target-hint" className="text-xs text-muted-foreground">
                    Get the hotel ID and branch ID from your Netera contact.
                </p>
                <Button ref={addButton} type="button" variant="outline" size="sm" data-testid="add-new-target" onClick={onAdd}>
                    <PlusIcon className="h-4 w-4" aria-hidden="true" />
                    Add another target
                </Button>
            </div>
        </fieldset>
    );
}

function AccountForm({
    draft,
    failure,
    onChange,
    onCancel,
    onContinue,
}: {
    draft: Draft;
    failure: Failure | null;
    onChange: (draft: Draft) => void;
    onCancel: () => void;
    onContinue: () => void;
}) {
    const errorId = `${useId()}-error`;
    const nextKey = Math.max(...draft.targets.map((target) => target.key)) + 1;
    const invalidFieldsErrorId = failure?.invalidInput ? errorId : undefined;

    const submit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isComplete(draft)) {
            onContinue();
        }
    };

    const replaceTarget = (changed: DraftTarget) =>
        onChange({ ...draft, targets: draft.targets.map((target) => (target.key === changed.key ? changed : target)) });

    return (
        <>
            <DialogHeader>
                <DialogTitle>Add a Netera account</DialogTitle>
                <DialogDescription>Keros logs in to Netera with these details to file invoices to MyData.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-5" aria-describedby={failure ? errorId : undefined}>
                <p className="text-xs text-muted-foreground">All fields are required.</p>
                <CredentialFields draft={draft} errorId={invalidFieldsErrorId} onChange={onChange} />
                <TargetsFieldset
                    targets={draft.targets}
                    errorId={invalidFieldsErrorId}
                    onReplace={replaceTarget}
                    onRemove={(key) => onChange({ ...draft, targets: draft.targets.filter((target) => target.key !== key) })}
                    onAdd={() => onChange({ ...draft, targets: [...draft.targets, blankTarget(nextKey)] })}
                />

                {failure && (
                    <FieldError id={errorId} testId="add-netera-account-error">
                        {failure.message}
                    </FieldError>
                )}

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" data-testid="add-netera-account-continue" disabled={!isComplete(draft)}>
                        Continue
                    </Button>
                </DialogFooter>
            </form>
        </>
    );
}

function AccountConfirmation({
    account,
    isSaving,
    onBack,
    onConfirm,
}: {
    account: CreateNeteraAccountRequest;
    isSaving: boolean;
    onBack: () => void;
    onConfirm: () => void;
}) {
    return (
        <>
            <DialogHeader>
                <DialogTitle>Check the filing targets</DialogTitle>
                <DialogDescription>
                    Netera account{" "}
                    <span data-testid="confirm-netera-account-username" className="font-medium text-foreground">
                        {account.username}
                    </span>
                </DialogDescription>
            </DialogHeader>
            <div className="overflow-hidden rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                            <TableHead>Target</TableHead>
                            <TableHead>Hotel ID</TableHead>
                            <TableHead>Branch ID</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {account.targets.map((target, index) => (
                            <TableRow key={`${target.hotelId}-${target.branchId}-${index}`} data-testid={`confirm-netera-target-${index + 1}`}>
                                <TableCell className="font-medium">{target.label}</TableCell>
                                <TableCell className="tabular-nums">{target.hotelId}</TableCell>
                                <TableCell className="tabular-nums">{target.branchId}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            <Note tone="warning">
                <p data-testid="confirm-netera-account-warning">
                    Confirm these hotel and branch IDs with your Netera contact before you continue. Uploads for linked
                    apartments are filed under them.
                </p>
            </Note>
            <DialogFooter>
                <Button type="button" variant="outline" data-testid="confirm-netera-account-back" onClick={onBack} disabled={isSaving}>
                    Go back
                </Button>
                <Button type="button" data-testid="confirm-netera-account-submit" onClick={onConfirm} disabled={isSaving}>
                    {isSaving ? "Adding account…" : "Add account"}
                </Button>
            </DialogFooter>
        </>
    );
}

export function AddNeteraAccountDialog({ onClose, onAdded }: { onClose: () => void; onAdded: (username: string) => void }) {
    const [draft, setDraft] = useState<Draft>({ username: "", password: "", apiKey: "", targets: [blankTarget(0)] });
    const [step, setStep] = useState<"form" | "confirm">("form");
    const [failure, setFailure] = useState<Failure | null>(null);
    const [createAccount, { isLoading: isSaving }] = useCreateNeteraAccountMutation();
    const request = requestFrom(draft);

    const confirm = async () => {
        const result = await createAccount(request);
        if (result.error) {
            setFailure(creationFailure(result.error));
            setStep("form");
            return;
        }
        onAdded(request.username);
    };

    return (
        <Dialog open onOpenChange={(open) => !open && !isSaving && onClose()}>
            <DialogContent
                data-testid={step === "form" ? "add-netera-account-dialog" : "confirm-netera-account-dialog"}
                className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-[560px]"
            >
                {step === "form" ? (
                    <AccountForm
                        draft={draft}
                        failure={failure}
                        onChange={setDraft}
                        onCancel={onClose}
                        onContinue={() => {
                            setFailure(null);
                            setStep("confirm");
                        }}
                    />
                ) : (
                    <AccountConfirmation
                        account={request}
                        isSaving={isSaving}
                        onBack={() => setStep("form")}
                        onConfirm={() => void confirm()}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
